import type { Platform } from './types';
import { PlatformUnavailableError } from './types';

/**
 * The browser build — `npm run dev`, and the hosted copy that lives on a
 * phone's home screen.
 *
 * This replaces the old `unavailable.ts`, which implemented nothing. Most of
 * what the native layer does turns out to have a perfectly good browser
 * equivalent; only the two that exist *because* a browser can't do them are
 * still missing.
 *
 * What a browser cannot do is read a shop's page. That is the whole reason
 * this app has a Rust half — CORS blocks the fetch and no header changes it —
 * so link import stays a desktop feature and says so.
 *
 * `showDataFolder` is absent rather than failing: there is no folder to open,
 * so the button that calls it isn't drawn. The Platform type marks it optional
 * for exactly this reason.
 */

const NO_NATIVE_HTTP =
  'link import reads the shop’s own page, which a browser is not allowed to fetch';

export const webPlatform: Platform = {
  fetchUrl: () => Promise.reject(new PlatformUnavailableError('fetchUrl', NO_NATIVE_HTTP)),

  fetchBinary: () => Promise.reject(new PlatformUnavailableError('fetchBinary', NO_NATIVE_HTTP)),

  /**
   * A download, which is the browser's version of a save dialog. Where it
   * lands is the browser's business; on iOS that means Files.
   *
   * Always resolves true. The native side reports false when the user
   * dismisses the panel, and a browser gives no such signal — once the click
   * is handed off there is nothing further to hear about. Reporting a
   * cancellation we cannot detect would be worse than reporting none.
   *
   * The `createObjectURL` here is the one outside `useObjectUrl`. That rule
   * exists so image URLs can't leak while a board is scrolling; this is a
   * single URL for a single file, revoked on the next turn of the event loop,
   * and it never belongs to a component that could unmount out from under it.
   */
  saveFile: async (name, data) => {
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');

    link.href = url;
    link.download = name;
    link.rel = 'noopener';

    document.body.append(link);
    link.click();
    link.remove();

    // Revoking in the same tick cancels the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  },

  openExternal: async (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  },
};
